import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { getPublicLocation } from "../../api-calls/location-share-requests";

// Module-level constant: @react-google-maps/api warns/reloads the script if
// the `libraries` array is a new reference on every render.
const GOOGLE_MAPS_LIBRARIES = [];
const POLL_INTERVAL_MS = 15000;

function formatUpdatedAt(updatedAt) {
  if (!updatedAt) return "";
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000)
  );
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function CenteredMessage({ children }) {
  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        fontFamily: "sans-serif",
        color: "#333",
      }}
    >
      <p>{children}</p>
    </div>
  );
}

function ShareLocationMap({ token }) {
  const [location, setLocation] = useState(null);
  const [inactive, setInactive] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  // Public endpoint re-validates the token AND the attendance session on
  // every call — a 410 here means the player checked out (or the link was
  // revoked), at which point we stop polling entirely.
  const fetchLocation = useCallback(async () => {
    try {
      const { data } = await getPublicLocation(token);
      setLocation(data);
      setInactive(false);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410 || status === 404) {
        setInactive(true);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else {
        setError("Unable to load this location right now.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLocation();
    pollRef.current = setInterval(fetchLocation, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchLocation]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  if (loading) {
    return <CenteredMessage>Loading location…</CenteredMessage>;
  }

  if (inactive) {
    return (
      <CenteredMessage>
        This location is no longer being shared. The player has checked out.
      </CenteredMessage>
    );
  }

  if (error || !location) {
    return (
      <CenteredMessage>
        {error || "No location data available yet."}
      </CenteredMessage>
    );
  }

  const center = {
    lat: Number(location.latitude),
    lng: Number(location.longitude),
  };

  return (
    <div style={{ height: "100vh", width: "100vw", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          background: "rgba(255,255,255,0.92)",
          padding: "12px 16px",
          fontFamily: "sans-serif",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
        }}
      >
        <strong>{location.name}</strong>
        <span style={{ marginLeft: 8, opacity: 0.7 }}>
          Updated {formatUpdatedAt(location.updatedAt)}
        </span>
      </div>
      {isLoaded ? (
        <GoogleMap
          center={center}
          zoom={15}
          mapContainerStyle={{ height: "100%", width: "100%" }}
          options={{ streetViewControl: false, mapTypeControl: false }}
        >
          <Marker position={center} />
        </GoogleMap>
      ) : (
        <CenteredMessage>Loading map…</CenteredMessage>
      )}
    </div>
  );
}

// Public, unauthenticated page — reachable via a share link generated by a
// player from the mobile app while they're checked in. No session, no
// Redux, no auth-gated layout: this page must render for a signed-out
// visitor exactly as well as for a logged-in team leader who clicks the
// same link.
export default function ShareLocationPage() {
  const { token } = useParams();

  if (!token) {
    return <CenteredMessage>Invalid location link.</CenteredMessage>;
  }

  return <ShareLocationMap token={token} />;
}
